import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PaymentFormComponent} from '../../../payment-form/payment-form.component';
import {FormsModule} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../toast/toast.service';
import {finalize, firstValueFrom, Subject, takeUntil} from 'rxjs';
import {Product} from '../../../../models/product';
import {Availability} from '../../../../models/schedule';
import {Room} from '../../../../models/room';
import {Router, RouterLink} from '@angular/router';
import {MomentPipe} from '../../../pipes/moment.pipe';
import {BookingPrice} from '../../../../models/price';
import {MoneyPipe} from '../../../pipes/money.pipe';
import {CurrencyPipe, NgIf} from '@angular/common';
import {UserService} from '../../../../services/user-service.service';
import {LeadService} from '../../../../services/lead.service';
import {OrderService} from '../../../../services/order.service';
import {RegionService} from '../../../../services/region-service';
import {AuthService} from '../../../../services/auth.service';
import {StorageService} from '../../../../services/storage-service';

import moment from "moment"
import {Lead} from '../../../../models/lead';
import {AnalyticsService} from '../../../../services/analytic.service';

const REFERENCE = "WEB_BOOKING_JOIN_MODAL"

@Component({
  selector: 'app-booking-join-modal',
  imports: [
    PaymentFormComponent,
    FormsModule,
    RouterLink,
    MomentPipe,
    MoneyPipe,
    NgIf
  ],
  providers: [CurrencyPipe],
  templateUrl: './booking-join-modal.component.html',
  styleUrl: './booking-join-modal.component.scss'
})
export class BookingJoinModalComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  @Input() regionSlug!: string
  @Input() room!: Room
  @Input() product!: Product
  @Input() promotion: Product | null = null
  @Input() availability!: Availability
  @Input() bookingPrice!: BookingPrice

  // Lead
  isLeadLoading = false
  isLeadLoaded = false
  leadId: string | null = null
  lead: Lead | null = null
  ref: string | null = null

  // User
  fullName: string = ""
  firstName: string | null = null
  lastName: string | null = null
  email: string | null = null
  password: string | null = null
  hometown: string | null = null

  loginRedirectPath = "/login"

  // Order
  submitted = false;

  // Wallet
  @ViewChild(PaymentFormComponent) paymentForm!: PaymentFormComponent;
  isPaymentFormLoaded = false
  isPaymentFormValid = false

  constructor(private modal: NgbActiveModal,
              private router: Router,
              private toast: ToastService,
              private storage: StorageService,
              private leadService: LeadService,
              private userService: UserService,
              private authService: AuthService,
              private regionService: RegionService,
              private orderService: OrderService,
              private analyticService: AnalyticsService) {
  }

  ngOnInit() {
    this.leadId = this.storage.get(StorageService.LEAD_ID) || null;
    this.ref = this.storage.get(StorageService.REF) || null;

    this.refreshLead()
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshLead() {
    if (!this.leadId) {
      return;
    }

    this.isLeadLoading = true;
    this.leadService
      .getById(this.leadId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.isLeadLoading = false
          this.isLeadLoaded = true
        }))
      .subscribe({
        next: (lead) => {
          this.lead = lead;

          this.email = lead.email || null;
          this.firstName = lead.firstName || null;
          this.lastName = lead.lastName || null;
          this.hometown = lead.hometown || null;

          if (this.firstName && this.lastName) {
            this.fullName = this.firstName + " " + this.lastName
          }
        },
        error: () => {
          this.storage.remove(StorageService.LEAD_ID);
        }
      });
  }

  async submit() {
    // Validate full name
    const fullNameTokens = this.fullName.split(' ').filter(token => token.length > 0);
    if (fullNameTokens.length <= 1) {
      this.toast.failure('Your first and last name are required.');
      return;
    }

    this.firstName = fullNameTokens[0];
    fullNameTokens.shift();
    this.lastName = fullNameTokens.join(' ');

    if (!this.product) {
      return this.toast.failure("Please select a product")
    }

    if (!this.isPaymentFormValid) {
      return this.toast.failure("Please provide a valid payment")
    }

    this.submitted = true;
    try {
      await this.createLeadPromise();
      await this.createUserPromise();
      await this.loginPromise();
      await this.paymentForm.onCreateOrRetrieveWallet();
      await this.createOrderPromise();

      // Clear the sign-up flow
      this.storage.remove(StorageService.LEAD_ID);
      this.storage.remove(StorageService.REF);
      this.storage.remove(StorageService.PRODUCT_SLUG);
      this.storage.remove(StorageService.PROMOTION_CODE);

      // Update analytic conversion
      this.analyticService.conversionCustomer()

      this.toast.success('Success! Check your email for the confirmation.');
      this.router.navigate(['/activity']).finally();

      // Close modal
      this.close()
    } catch (error: any) {

      // Logout
      this.authService.logout();

      // Display the error message
      let message = 'There was a problem. Please refresh and try again or contact support for assistance.';

      if (error && error.error && error.error.message) {
        message = error.error.message.toString();
      } else if (error && error.error && error.error.error && error.error.error.message) {
        message = error.error.error.message.toString();
      } else if (error && error.data && error.data.error && error.data.error.message) {
        message = error.data.error.message.toString();
      } else if (error) {
        message = error.toString();
      }

      this.toast.failure(message);
    } finally {
      this.submitted = false;
    }
  }

  createLeadPromise() {
    if (!this.email) {
      return Promise.reject("Email is required")
    }

    if (!this.firstName) {
      return Promise.reject("First name is required")
    }

    if (!this.lastName) {
      return Promise.reject("Last name is required")
    }

    if (!this.regionSlug) {
      return Promise.reject("Region is required")
    }

    const email = this.email!.toLowerCase();
    const reference = this.leadService.generateReference(REFERENCE)
    const firstName = this.firstName!;
    const lastName = this.lastName!;
    const regionSlug = this.regionSlug!;

    return new Promise((resolve, reject) => {
      this.leadService.createRegionLead(email, reference, firstName, lastName, regionSlug)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (createdLead) => {
            this.storage.add(StorageService.LEAD_ID, createdLead._id);
            this.regionService.regionSlug$.next(this.regionSlug);

            // TODO -- Add analytics service
            // this.analyticsService.lead(this.form.value.email);

            return resolve(createdLead);
          },
          error: (error: any) => {
            return reject(error);
          }
        });
    });
  }

  createUserPromise() {
    if (!this.firstName) {
      return Promise.reject("First name is required")
    }

    if (!this.lastName) {
      return Promise.reject("Last name is required")
    }

    if (!this.email) {
      return Promise.reject("Email is required")
    }

    if (!this.password) {
      return Promise.reject("Password is required")
    }

    if (!this.regionSlug) {
      return Promise.reject("Region is required")
    }

    const firstName = this.firstName!;
    const lastName = this.lastName!;
    const email = this.email!.toLowerCase();
    const password = this.password!;
    const regionSlug = this.regionSlug!;
    const hometown = this.hometown || null; // Not captured

    return firstValueFrom(this.userService.createCustomer(email, password, firstName, lastName, regionSlug, hometown, true)
      .pipe(takeUntil(this.onDestroy$)))
  }

  async loginPromise() {
    if (!this.email) {
      return Promise.reject("Email is required")
    }

    if (!this.password) {
      return Promise.reject("Password is required")
    }

    const email = this.email!;
    const password = this.password!;

    return firstValueFrom(this.authService.login(email, password)
      .pipe(takeUntil(this.onDestroy$)))
  }

  async createOrderPromise() {
    if (!this.product) {
      return Promise.reject("Product is required")
    }

    if (!this.room) {
      return Promise.reject("Room is required")
    }

    if (!this.availability) {
      return Promise.reject("Availability is required")
    }

    if (!this.bookingPrice) {
      return Promise.reject("Booking price is required")
    }

    const productIds: string[] = this.product ? [this.product._id!] : [];
    const referralIds: string[] = []; // Disabled since there's not going to be any referrals occurring
    const promotionIds: string[] = this.promotion ? [this.promotion._id!] : []
    const placeId = this.room.place!._id!;
    const roomId = this.room._id!;
    const localBegin = moment(this.availability.localBegin, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
    const localEnd = moment(this.availability.localEnd, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
    const totalPrice = this.bookingPrice.totalBalance;

    return firstValueFrom(this.orderService.createBookingOrder(productIds, referralIds, promotionIds, placeId, roomId, localBegin, localEnd, totalPrice)
      .pipe(takeUntil(this.onDestroy$)))
  }

  onPaymentFormLoaded(loaded: boolean) {
    this.isPaymentFormLoaded = loaded
  }

  onPaymentFormValid(valid: boolean) {
    this.isPaymentFormValid = valid
  }

  close() {
    this.modal.close()
  }

}
